import { useReducedMotion } from 'framer-motion'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card } from '@/design-system/primitives'
import { formatTime } from '@/lib/datetime'
import { cn } from '@/lib/cn'
import { useApiErrorText } from '@/lib/staffGate'

import {
  useContestBoard,
  useContests,
  useFlowAction,
  usePlanChange,
  useScreenPreview,
  useSegments,
  type FlowAction,
} from '../api'
import { ContestControl } from '../ContestControl'
import { durationMinutes, shiftCount, type LiveTiming } from '../plan'
import { ShiftControl } from '../PlanEditor'
import type { ConductorItem, ConductorRound } from '../types'
import { LiveSegments, OverlayBanner, segmentsByItem } from './Segments'
import { SectionHead, text } from './shared'

const STATUS_TONES: Record<string, string> = {
  scheduled: 'border-line-subtle',
  running: 'border-2 border-ember-400',
  done: 'border-line-subtle opacity-50',
  skipped: 'border-line-subtle opacity-40',
}

/** En deçà, un écart n'est pas un retard : la salle ne le voit pas. */
const DELAY_TOLERANCE = 3

/**
 * Le tour se joue pendant cette étape — les deux liens comptent, comme au
 * serveur : le tour désigne son étape, ou l'étape désigne son tour.
 */
function belongsTo(round: ConductorRound, item: ConductorItem): boolean {
  return (
    round.program_item_id === item.id ||
    (item.kind === 'competition_round' && item.ref_id === round.id)
  )
}

const stepAnchor = (id: string) => `etape-${id}`

/**
 * Dérouler : l'étape en cours, la suite, et la liste complète.
 *
 * La personne qui tient cet écran a un micro dans l'autre main. Donc : une
 * action par étape, aucune boîte de confirmation, et tout est réversible.
 */
export function LiveTab({
  eventId,
  items,
  timing,
  timezone,
  isPending,
  can,
}: {
  eventId: string | undefined
  items: ConductorItem[]
  timing: LiveTiming
  timezone: string
  isPending: boolean
  can: (permission: string) => boolean
}) {
  const { t, i18n } = useTranslation()
  const errorText = useApiErrorText()
  const flow = useFlowAction(eventId)
  const change = usePlanChange(eventId)
  const [shifting, setShifting] = useState(false)
  const locale = i18n.language
  const canFlow = can('program.flow.control')
  const canShift = can('program.item.update')
  // La feuille du MC : lue par qui déroule ou prépare la soirée.
  const canSheet = canFlow || canShift
  const segments = useSegments(eventId, canSheet)
  const contests = useContests(eventId, canSheet)
  const byItem = useMemo(() => segmentsByItem(segments.data), [segments.data])
  // Ce qui est posé à l'écran en ce moment : un pronostic oublié cacherait
  // le programme à la salle — on le rappelle ici, avec son bouton « retirer ».
  const preview = useScreenPreview(eventId, can('program.screen.control'))
  const [opened, setOpened] = useState<Set<string>>(() => new Set())
  const toggle = (id: string) =>
    setOpened((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const act = (item: ConductorItem, action: FlowAction) => {
    flow.mutate({ itemId: item.id, action })
  }

  const { running, next, delay } = timing
  // Le retard proposé d'office, arrondi aux cinq minutes du dessus : on
  // décale d'un chiffre qu'on peut annoncer, pas de « 7 min ».
  // Au-delà de quatre heures, ce n'est plus un retard mais une étape lancée
  // hors de la soirée (un essai) : le serveur refuserait d'ailleurs le décalage.
  const suggested =
    delay !== null && delay >= DELAY_TOLERANCE && delay <= 240 ? Math.ceil(delay / 5) * 5 : null
  const pivot = next?.starts_at ? next : null
  // L'étape dont la feuille s'affiche en grand : celle en cours, sinon la
  // suivante (le pronostic se fait juste AVANT le premier tour).
  const focus = running ?? next
  const focusSegments = focus ? (byItem.get(focus.id) ?? []) : []

  // Le concours se pilote DANS la carte de l'étape lancée : le défilé, les
  // votes et leur décompte sous le titre « Miss & Master », pas en haut de
  // la page, loin de l'étape. Ce qui ne s'y rattache pas (un tour démarré à
  // la main depuis « Concours », ou un départage sans étape en cours) garde
  // un bloc à part, en tête et nommé comme tel — il ne doit pas disparaître.
  const board = useContestBoard(eventId, true)
  const rounds = board.data?.rounds ?? []
  const stepRounds = running ? rounds.filter((round) => belongsTo(round, running)) : []
  const strayRounds = rounds.filter(
    (round) => round.status === 'running' && !(running && belongsTo(round, running)),
  )
  const openTiebreaks = (board.data?.votes ?? []).filter(
    (vote) => vote.is_tiebreak && vote.status === 'open',
  )
  const strayTiebreaks = running ? [] : openTiebreaks
  // Les départages ouverts suivent l'étape en cours, quelle qu'elle soit :
  // c'est ce qui se joue sur scène à cet instant.
  const runningHasLive = stepRounds.length > 0 || openTiebreaks.length > 0
  const reduceMotion = useReducedMotion()
  const jumpTo = (id: string) =>
    document
      .getElementById(stepAnchor(id))
      ?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })

  return (
    <div className="space-y-6">
      {/* Le retard, et le geste qui le résorbe : tout ce qui reste à venir
          glisse d'un bloc. */}
      {canShift && pivot && suggested !== null ? (
        <Card className="border-state-warning/50 p-4">
          <p className="text-sm text-content-primary">
            {t('conductor.cockpit.live.lateBy', { minutes: delay })}
          </p>
          {shifting ? (
            <ShiftControl
              count={shiftCount(items, pivot.id)}
              from={text(pivot.title_i18n, locale)}
              initial={suggested}
              pending={change.isPending}
              onCancel={() => setShifting(false)}
              onShift={(minutes) =>
                change.mutate(
                  { kind: 'shift', id: pivot.id, minutes },
                  { onSuccess: () => setShifting(false) },
                )
              }
            />
          ) : (
            <Button className="mt-3 w-full sm:w-auto" variant="outline" onClick={() => setShifting(true)}>
              {t('conductor.cockpit.live.shiftBy', { minutes: suggested })}
            </Button>
          )}
          {change.isError ? (
            <p className="mt-2 text-sm text-state-danger">{errorText(change.error)}</p>
          ) : null}
        </Card>
      ) : null}

      {/* Les gestes du moment, en tête : finir l'étape, lancer la suivante. */}
      {canFlow && (running || next) ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {running ? (
            <Button variant="outline" disabled={flow.isPending} onClick={() => act(running, 'end')}>
              {t('conductor.cockpit.live.endCurrent')}
            </Button>
          ) : null}
          {next ? (
            <Button disabled={flow.isPending} onClick={() => act(next, 'start')}>
              <span className="min-w-0 truncate">
                {t('conductor.cockpit.live.startNext', { title: text(next.title_i18n, locale) })}
              </span>
            </Button>
          ) : null}
        </div>
      ) : null}

      {flow.isError ? (
        <p role="alert" className="text-sm text-state-danger">
          {errorText(flow.error)}
        </p>
      ) : null}

      <OverlayBanner eventId={eventId} overlay={preview.data?.overlay} can={can} />

      {/* L'étape lancée peut être loin dans la liste : un appui y mène, au
          lieu de faire défiler dix cartes micro en main. */}
      {focus && (focusSegments.length > 0 || (focus === running && runningHasLive)) ? (
        <Button
          variant="outline"
          className="w-full min-w-0 justify-between sm:w-auto"
          onClick={() => jumpTo(focus.id)}
        >
          <span className="min-w-0 truncate">
            {t(running ? 'conductor.cockpit.live.jumpRunning' : 'conductor.cockpit.live.jumpNext', {
              title: text(focus.title_i18n, locale),
            })}
          </span>
          <span aria-hidden className="shrink-0">
            ↓
          </span>
        </Button>
      ) : null}

      {/* Sur scène, mais hors de l'étape lancée : rare, et nommé comme tel. */}
      {strayRounds.length > 0 || strayTiebreaks.length > 0 ? (
        <Card className="space-y-3 border-ember-400/60 p-4 sm:p-5">
          <SectionHead
            title={t('conductor.cockpit.live.strayTitle')}
            hint={t('conductor.cockpit.live.strayHint')}
          />
          <ContestControl
            eventId={eventId}
            can={can}
            rounds={(round) => strayRounds.some((entry) => entry.id === round.id)}
            tiebreaks={running ? 'none' : 'open'}
            bare
          />
        </Card>
      ) : null}

      <ol className="space-y-3">
        {items.map((item) => {
          const minutes = durationMinutes(item)
          const isFocus = item.id === focus?.id
          const sheet = isFocus ? focusSegments : []
          const live = item.id === running?.id && runningHasLive
          return (
            <li key={item.id} id={stepAnchor(item.id)} className="scroll-mt-56">
              <Card className={cn('p-4 sm:p-5', STATUS_TONES[item.status] ?? '')}>
                <div className="flex items-start gap-3">
                  <div className="w-16 shrink-0 pt-0.5">
                    <p className="font-mono text-sm tabular-nums text-champagne-300">
                      {item.starts_at ? formatTime(item.starts_at, locale, timezone) : '—'}
                    </p>
                    {minutes !== null ? (
                      <p className="whitespace-nowrap text-caption text-content-muted">
                        {t('conductor.plan.minutes', { count: minutes })}
                      </p>
                    ) : null}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3 className="break-words font-display text-heading-sm text-content-primary">
                      {text(item.title_i18n, locale)}
                    </h3>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {/* Une étape interne ne doit jamais être confondue avec
                          ce que la salle voit. */}
                      {!item.is_public ? <Badge>{t('conductor.internal')}</Badge> : null}
                      {item.status === 'running' ? (
                        <Badge tone="ember">{t('program.running')}</Badge>
                      ) : null}
                      {item.status === 'done' ? (
                        <Badge tone="success">{t('conductor.done')}</Badge>
                      ) : null}
                      {item.status === 'skipped' ? (
                        <Badge tone="neutral">{t('program.skipped')}</Badge>
                      ) : null}
                    </div>
                    {item.host || item.location ? (
                      <p className="mt-1 break-words text-caption text-content-muted">
                        {[item.host, item.location].filter(Boolean).join(' · ')}
                      </p>
                    ) : null}
                    {item.notes ? (
                      <p className="mt-1 whitespace-pre-line break-words text-caption text-content-secondary">
                        {item.notes}
                      </p>
                    ) : null}
                    {item.actual_started_at ? (
                      <p className="mt-1 text-caption text-content-muted">
                        {t('conductor.cockpit.live.actual', {
                          start: formatTime(item.actual_started_at, locale, timezone),
                          end: item.actual_ended_at
                            ? formatTime(item.actual_ended_at, locale, timezone)
                            : '…',
                        })}
                      </p>
                    ) : null}

                    {!isFocus && (byItem.get(item.id)?.length ?? 0) > 0 ? (
                      <div className="mt-3">
                        <Button
                          size="sm"
                          variant={opened.has(item.id) ? 'champagne' : 'outline'}
                          aria-expanded={opened.has(item.id)}
                          onClick={() => toggle(item.id)}
                        >
                          {t('conductor.segments.toggle', {
                            count: byItem.get(item.id)?.length ?? 0,
                          })}
                        </Button>
                      </div>
                    ) : null}

                    {canFlow ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {item.status === 'scheduled' ? (
                          <>
                            <Button size="sm" disabled={flow.isPending} onClick={() => act(item, 'start')}>
                              {t('conductor.start')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={flow.isPending}
                              onClick={() => act(item, 'skip')}
                            >
                              {t('conductor.skip')}
                            </Button>
                          </>
                        ) : item.status === 'running' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={flow.isPending}
                            onClick={() => act(item, 'end')}
                          >
                            {t('conductor.end')}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={flow.isPending}
                            onClick={() => act(item, 'reset')}
                          >
                            {t('conductor.reset')}
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Toute la largeur de la carte, sous son en-tête : sur un
                    téléphone, la colonne de l'heure volerait au défilé et
                    aux boutons du vote la place d'un pouce. */}
                {live ? (
                  <section
                    aria-label={t('conductor.cockpit.live.onStage')}
                    className="mt-4 space-y-3 border-t border-line-subtle pt-4"
                  >
                    <p className="overline text-ember-300">{t('conductor.cockpit.live.onStage')}</p>
                    <ContestControl
                      eventId={eventId}
                      can={can}
                      rounds={(round) => belongsTo(round, item)}
                      tiebreaks="open"
                      bare
                    />
                  </section>
                ) : null}

                {/* La feuille de l'étape, sous les yeux du MC : ce qu'il lisait
                    sur papier, avec les gestes d'écran qui l'accompagnent. */}
                {sheet.length > 0 ? (
                  <section
                    aria-label={
                      running ? t('conductor.segments.liveTitle') : t('conductor.segments.nextTitle')
                    }
                    className="mt-4 space-y-3 border-t border-line-subtle pt-4"
                  >
                    <p className="overline text-champagne-300">
                      {running
                        ? t('conductor.segments.liveTitle')
                        : t('conductor.segments.nextTitle')}
                    </p>
                    <LiveSegments
                      eventId={eventId}
                      segments={sheet}
                      contests={contests.data ?? []}
                      can={can}
                    />
                  </section>
                ) : null}

                {!isFocus && opened.has(item.id) ? (
                  <div className="mt-4 border-t border-line-subtle pt-4">
                    <LiveSegments
                      eventId={eventId}
                      segments={byItem.get(item.id) ?? []}
                      contests={contests.data ?? []}
                      can={can}
                    />
                  </div>
                ) : null}
              </Card>
            </li>
          )
        })}
      </ol>

      {items.length === 0 && !isPending ? (
        <p className="text-content-secondary">{t('conductor.empty')}</p>
      ) : null}
    </div>
  )
}
